import React from 'react';
import { Link } from 'react-router-dom';
import './Button.css';

/**
 * Button primitive — single source of truth for all CTAs across
 * the storefront. Replaces the legacy `.btn` / `.btn-primary` /
 * `.btn-secondary` selectors that used to ship from index.css.
 *
 * Variants:
 *  - `primary`   — dark ink fill, white text. Top-of-page CTAs.
 *  - `secondary` — hairline outline, ink text. Side actions.
 *  - `ghost`     — transparent, ink text on hover. Tertiary.
 *  - `brand`     — forest-green fill, for confirmations / success
 *                  paths (used sparingly so the brand color reads
 *                  as accent, not chrome).
 *
 * Sizes:
 *  - `sm` — 32px tall, smaller padding
 *  - `md` — 44px tall (touch target), default
 *  - `lg` — 52px tall, generous padding (hero CTAs)
 *
 * `as` prop polymorphism: pass `as="link"` + `to=""` to render as
 * a `react-router` Link; otherwise renders as `<button>`. Anchors
 * (external) use `as="a"` + `href`. The component never renders
 * a router Link as a child of another router Link, so `<a>` /
 * `<button>` / `<Link>` are mutually exclusive.
 */
const Button = React.forwardRef(({
  variant = 'primary',
  size = 'md',
  as = 'button',
  to,
  href,
  type,
  className = '',
  startIcon,
  endIcon,
  children,
  fullWidth = false,
  ...rest
}, ref) => {
  const classes = [
    'btn-primitive',
    `btn-primitive--${variant}`,
    `btn-primitive--${size}`,
    fullWidth ? 'btn-primitive--full' : '',
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {startIcon && <span className="btn-primitive__icon" aria-hidden="true">{startIcon}</span>}
      <span className="btn-primitive__label">{children}</span>
      {endIcon && <span className="btn-primitive__icon" aria-hidden="true">{endIcon}</span>}
    </>
  );

  if (as === 'link' && to) {
    return (
      <Link ref={ref} to={to} className={classes} {...rest}>{content}</Link>
    );
  }
  if (as === 'a' && href) {
    return (
      <a ref={ref} href={href} className={classes} {...rest}>{content}</a>
    );
  }
  return (
    <button ref={ref} type={type || 'button'} className={classes} {...rest}>{content}</button>
  );
});

Button.displayName = 'Button';

export default Button;
